import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react'
import RangeSlider from 'react-range-slider-input';

export default observer(function SpecialSlider({ label, min, max, onInput, leftValue, rightValue }) {
  // 1) Értékskála előállítása fix lépésekkel (500, 5000, 50000, 100000)
  const ticks = useMemo(() => {
    const values = []

    const pushRange = (start, end, step, includeStart = true) => {
      let current = includeStart ? start : start + step
      while (current <= end) {
        // Kerüljük a duplikált értékeket
        if (values.length === 0 || values[values.length - 1] !== current) values.push(current)
        current += step
      }
    }

    // 1K → 10K (500)
    pushRange(1000, 10000, 500, true)
    // 10K → 100K (5000)
    pushRange(10000, 100000, 5000, false)
    // 100K → 1M (50000)
    pushRange(100000, 1000000, 50000, false)
    // 1M → 10M (500000)
    pushRange(1000000, 10000000, 500000, false)

    return values
  }, [])

  // 2) Segédfüggvények indexekhez
  const findNearestIndex = (value) => {
    // Bináris keresés egyszerűsítve
    let lo = 0
    let hi = ticks.length - 1
    while (lo < hi) {
      const mid = Math.floor((lo + hi) / 2)
      if (ticks[mid] < value) lo = mid + 1
      else hi = mid
    }
    // Válasszuk a közelebbit
    if (lo > 0 && Math.abs(ticks[lo - 1] - value) < Math.abs(ticks[lo] - value)) return lo - 1
    return lo
  }

  // 3) Állapot indexekkel
  const [minIndex, setMinIndex] = useState(0)
  const [maxIndex, setMaxIndex] = useState(ticks.length - 1)

  // 4) Props változásra szinkronizáljuk az indexeket
  useEffect(() => {
    setMinIndex(findNearestIndex(Number(min)))
    setMaxIndex(findNearestIndex(Number(max)))
  }, [min, max, ticks])

  // 5) Slider változás → indexekből valós értékek
  const onRangeChange = ([fromIdx, toIdx]) => {
    // Clamp
    const clampedFrom = Math.max(0, Math.min(ticks.length - 1, Math.floor(fromIdx)))
    const clampedTo = Math.max(0, Math.min(ticks.length - 1, Math.floor(toIdx)))

    setMinIndex(clampedFrom)
    setMaxIndex(clampedTo)
    onInput([ticks[clampedFrom], ticks[clampedTo]])
  }

  // 6) Aktuális értékek és pozíciók megjelenítéshez
  const currentMinValue = ticks[minIndex]
  const currentMaxValue = ticks[maxIndex]
  const minLeftPercent = useMemo(() => (minIndex / (ticks.length - 1)) * 100, [minIndex, ticks.length])
  const maxLeftPercent = useMemo(() => (maxIndex / (ticks.length - 1)) * 100, [maxIndex, ticks.length])

  const formatCompact = (value) => {
    if (value >= 1_000_000) {
      const millions = Math.floor(value / 1_000_000)
      const rem = value % 1_000_000
      if (rem === 0) return `${millions}M`
      if (rem === 500_000) return `${millions},5M`
      // Fallback: egy tizedes (kerekítés)
      return `${(value / 1_000_000).toFixed(1).replace('.', ',')}M`
    }
    if (value >= 1_000) {
      const thousands = Math.floor(value / 1_000)
      const rem = value % 1_000
      if (rem === 0) return `${thousands}k`
      if (rem === 500) return `${thousands},5k`
      // Fallback: egy tizedes (kerekítés ezerre)
      return `${(value / 1_000).toFixed(1).replace('.', ',')}k`
    }
    return `${value}`
  }

  return (
    <div>
      <div className="flex justify-between items-center">
        {label ? <p className="font-semibold dark:text-white">{label}</p> : <span />}
        <div className="flex items-center justify-between gap-2">
          <div className="flex rounded-full items-center bg-rb-grey px-2 py-0.5 dark:bg-rb-dark-mode-lighter">
            <div className="rounded-full w-2.5 h-2.5 mr-1.5 bg-[#FFEC46]"/>
            <p className="text-xs font-medium text-rb-mat-grey dark:text-gray-300">Nano: 1-10k</p>
          </div>
          <div className="flex rounded-full items-center bg-rb-grey px-2 py-0.5 dark:bg-rb-dark-mode-lighter">
            <div className="rounded-full w-2.5 h-2.5 mr-1.5 bg-[#75FF94]" />
            <p className="text-xs font-medium text-rb-mat-grey dark:text-gray-300">Micro: 10-100k</p>
          </div>
          <div className="flex rounded-full items-center bg-rb-grey px-2 py-0.5 dark:bg-rb-dark-mode-lighter">
            <div className="rounded-full w-2.5 h-2.5 mr-1.5 bg-[#4CDFFF]" />
            <p className="text-xs font-medium text-rb-mat-grey dark:text-gray-300">Macro: 100k-1M</p>
          </div>
          <div className="flex rounded-full items-center bg-rb-grey px-2 py-0.5 dark:bg-rb-dark-mode-lighter">
            <div className="rounded-full w-2.5 h-2.5 mr-1.5 bg-[#6171FF]" />
            <p className="text-xs font-medium text-rb-mat-grey dark:text-gray-300">Mega: +1M</p>
          </div>
        </div>

      </div>
      <div className="w-full relative mt-9 mb-5">
        <RangeSlider
          min={0}
          max={ticks.length - 1}
          value={[minIndex, maxIndex]}
          onInput={onRangeChange}
          step={1}
        />

        {/* Aktuális érték label a bal thumb fölött */}
        <div
          className="absolute -top-8 text-xs font-semibold text-gray-700 bg-white dark:bg-rb-dark-mode-lighter dark:text-white px-2 py-1 rounded shadow pointer-events-none"
          style={{ left: `${minLeftPercent}%`, transform: 'translateX(-50%)' }}
        >
          {formatCompact(currentMinValue)}
        </div>

        {/* Aktuális érték label a jobb thumb fölött */}
        <div
          className="absolute -top-8 text-xs font-semibold text-gray-700 bg-white dark:bg-rb-dark-mode-lighter dark:text-white px-2 py-1 rounded shadow pointer-events-none"
          style={{ left: `${maxLeftPercent}%`, transform: 'translateX(-50%)' }}
        >
          {formatCompact(currentMaxValue)}
        </div>
        <div className="absolute w-full top-0 flex">
          <div className="bg-[#FFEC46] w-1/4 h-2 z-10 rounded-lg top-0"></div>
          <div className="bg-[#75FF94] w-1/4 h-2 z-10 rounded-lg top-0"></div>
          <div className="bg-[#4CDFFF] w-1/4 h-2 z-10 rounded-lg top-0"></div>
          <div className="bg-[#6171FF] w-1/4 h-2 z-10 rounded-lg top-0"></div>
        </div>

        <div className="absolute flex top-0 justify-evenly w-full">
          <div className="bg-white w-2 h-2 z-10 rounded-full"></div>
          <div className="bg-white w-2 h-2 z-10 rounded-full"></div>
          <div className="bg-white w-2 h-2 z-10 rounded-full"></div>
        </div>

        <div className="absolute top-4 text-xs text-gray-400 rounded-full left-0">{"<1k"}</div>
        <div className="absolute -ml-2.5 top-4 text-xs text-gray-400 rounded-full left-1/4">10k</div>
        <div className="absolute -ml-2.5 top-4 text-xs text-gray-400 rounded-full left-2/4">100k</div>
        <div className="absolute -ml-2.5 top-4 text-xs text-gray-400 rounded-full left-3/4">1M</div>
        <div className="absolute top-4 text-xs text-gray-400 rounded-full right-0">10M+</div>

      </div>
    </div>
  )
})